import { collection, query, where, getDocs, doc, getDoc, setDoc } from "firebase/firestore";
import { db } from "./firebase";

/**
 * SiliconLogix Unique Identifier (Silicon ID) Generator & Management
 * Format: SLX26-XXXXXXX (e.g. SLX26-8941203)
 * Permanent, unique identifier for every engineer and student on the platform.
 */

/**
 * Generates a candidate Silicon ID for a given year.
 * @param {number|string} [year] - Defaults to current year (2-digit suffix extracted e.g. 2026 -> 26).
 * @returns {string} - Formatted Silicon ID candidate (e.g. SLX26-8941203).
 */
export const generateCandidateSiliconId = (year = new Date().getFullYear()) => {
  const shortYear = String(year || new Date().getFullYear()).slice(-2);
  // Generate a random 7-digit number between 1000000 and 9999999
  const randomNum = Math.floor(1000000 + Math.random() * 9000000);
  return `SLX${shortYear}-${randomNum}`;
};

/**
 * Checks if a given Silicon ID already exists in the Firestore `users` collection.
 * @param {string} siliconId
 * @returns {Promise<boolean>}
 */
export const isSiliconIdTaken = async (siliconId) => {
  if (!siliconId || !db) return false;
  try {
    const usersRef = collection(db, "users");
    const q = query(usersRef, where("siliconId", "==", siliconId));
    const snap = await getDocs(q);
    return !snap.empty;
  } catch (err) {
    console.warn("[SiliconID] Error checking uniqueness:", err);
    return false;
  }
};

/**
 * Generates a guaranteed unique Silicon ID by checking against existing records.
 * Retries up to maxRetries in case of an extremely rare collision.
 * @param {number} [year]
 * @param {number} [maxRetries=5]
 * @returns {Promise<string>}
 */
export const generateUniqueSiliconId = async (year = new Date().getFullYear(), maxRetries = 5) => {
  for (let attempt = 0; attempt < maxRetries; attempt++) {
    const candidate = generateCandidateSiliconId(year);
    const taken = await isSiliconIdTaken(candidate);
    if (!taken) {
      return candidate;
    }
  }
  // Fallback with timestamp millisecond suffix if all retries collided
  const shortYear = String(year || new Date().getFullYear()).slice(-2);
  const timestampSuffix = String(Date.now()).slice(-7);
  return `SLX${shortYear}-${timestampSuffix}`;
};

/**
 * Retrieves the Silicon ID for a user, or generates and saves one if not already present.
 * Strictly guarantees permanency: checks memory AND Firestore before generating.
 * @param {string} uid
 * @param {Object} [userData]
 * @returns {Promise<string>}
 */
export const getOrAssignSiliconId = async (uid, userData = {}) => {
  if (!uid) return null;

  // 1. Return in-memory ID if already present
  if (userData?.siliconId) {
    return userData.siliconId;
  }

  try {
    if (db) {
      const userDocRef = doc(db, "users", uid);
      // 2. Inspect Firestore first to ensure permanent ID is preserved
      const userSnap = await getDoc(userDocRef);
      if (userSnap.exists()) {
        const existingData = userSnap.data();
        if (existingData?.siliconId) {
          return existingData.siliconId;
        }
      }

      // 3. Only if Firestore record has no siliconId, generate a unique one once
      const newSiliconId = await generateUniqueSiliconId();
      await setDoc(userDocRef, {
        siliconId: newSiliconId,
        updatedAt: new Date()
      }, { merge: true });
      return newSiliconId;
    }
    return null;
  } catch (err) {
    console.warn(`[SiliconID] Could not assign Silicon ID to ${uid}:`, err);
    return userData?.siliconId || null;
  }
};
